/**
 * Vehicle Selector (Model / Colour)
 *
 * Each Brand is its own separate product, listed individually in the
 * catalog. This component only handles the current product's own options —
 * there is no cross-product Brand switcher here.
 *
 * This component NEVER resolves variants itself and NEVER updates price,
 * media, SKU, inventory, or the Add to Cart / Buy Now buttons directly. It
 * only:
 *   1. Reads the product's variants (embedded as JSON by the Liquid snippet).
 *   2. Renders a Model dropdown and Colour cards from that data.
 *   3. On colour selection, sets the values of the theme's native
 *      <variant-picker> <select> elements and dispatches one native `change`
 *      event — exactly what happens when a shopper uses the native dropdowns.
 *
 * The native `variant-picker` component (assets/variant-picker.js) then takes
 * over: it fetches the updated section via Shopify's Section Rendering API and
 * every listener (media gallery, price, SKU, inventory, buy buttons, sticky
 * add-to-cart) updates itself through the standard ProductSelectEvent pipeline.
 */

(function () {
  'use strict';

  const TICK_SVG =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
    '<path d="M3.5 8.5l3 3 6-6.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  /**
   * @param {string} value
   * @returns {string}
   */
  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  class XdVehicleSelector {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;

      const variantsScript = root.querySelector('[data-xd-variants]');
      const configScript = root.querySelector('[data-xd-config]');

      if (!variantsScript || !configScript) {
        console.warn('[xd-vehicle-selector] Data script tags not found inside root element. Aborting init.', {
          root,
          variantsScriptFound: !!variantsScript,
          configScriptFound: !!configScript,
        });
        return;
      }

      try {
        this.variants = JSON.parse(variantsScript.textContent || '[]');
        this.config = JSON.parse(configScript.textContent || '{}');
      } catch (error) {
        console.error('[xd-vehicle-selector] Failed to parse embedded product data', error);
        return;
      }

      if (!Array.isArray(this.variants) || !this.variants.length) {
        console.warn('[xd-vehicle-selector] No variants found in embedded data. Aborting init.', this.variants);
        return;
      }

      this.modelSelect = root.querySelector('[data-xd-model]');
      this.coloursContainer = root.querySelector('[data-xd-colours]');
      this.coloursEmpty = root.querySelector('[data-xd-colours-empty]');
      this.hint = root.querySelector('[data-xd-hint]');

      if (!this.modelSelect || !this.coloursContainer) {
        console.warn('[xd-vehicle-selector] Required UI elements not found inside root element. Aborting init.', {
          modelSelectFound: !!this.modelSelect,
          coloursContainerFound: !!this.coloursContainer,
        });
        return;
      }

      this.nativePicker = null;
      this.state = { model: '', variantId: null };

      this.init();
      console.info('[xd-vehicle-selector] Initialized with', this.variants.length, 'variants.');
    }

    init() {
      this.findNativePicker();
      this.hideNativePicker();
      this.findBuyButtonForms();
      this.lockBuyButtons();
      this.populateModels();
      this.populateColours('');
      this.bindEvents();
    }

    // ---------------------------------------------------------------------
    // Native <variant-picker> lookup
    // ---------------------------------------------------------------------

    findNativePicker() {
      const sectionId = this.config.sectionId;
      const productId = this.config.productId;

      if (!sectionId || !productId) return;

      const selector =
        'variant-picker[data-section-id="' +
        CSS.escape(String(sectionId)) +
        '"][data-product-id="' +
        CSS.escape(String(productId)) +
        '"]';

      this.nativePicker = document.querySelector(selector);
    }

    hideNativePicker() {
      if (this.nativePicker) {
        this.nativePicker.classList.add('xd-native-picker--hidden');
      } else {
        console.warn(
          '[xd-vehicle-selector] Native <variant-picker> element not found for sectionId/productId — it will remain visible.',
          this.config
        );
      }
    }

    // ---------------------------------------------------------------------
    // Buy buttons lock — Add to Cart / Buy Now stay disabled until a colour
    // is explicitly chosen. The native pipeline morphs these elements fresh
    // from the server on every real variant change, so once the user picks a
    // colour and we dispatch the native `change` event, the real availability
    // state takes over automatically — we only own the "before" state here.
    // ---------------------------------------------------------------------

    findBuyButtonForms() {
      const productId = this.config.productId;
      let forms = [];

      if (productId) {
        const selector = 'product-form-component[data-product-id="' + CSS.escape(String(productId)) + '"]';
        forms = Array.from(document.querySelectorAll(selector));
      }

      if (!forms.length) {
        // Fallback: on a standard product page there is normally only one
        // <product-form-component>. If the scoped lookup found nothing
        // (e.g. a product-id mismatch), fall back to any on the page rather
        // than silently locking nothing.
        const fallback = Array.from(document.querySelectorAll('product-form-component'));
        if (fallback.length) {
          console.warn(
            '[xd-vehicle-selector] No <product-form-component> matched productId="' +
              productId +
              '". Falling back to all ' +
              fallback.length +
              ' found on the page. Check that data-product-id on your buy-buttons block matches.',
          );
          forms = fallback;
        } else {
          console.warn('[xd-vehicle-selector] No <product-form-component> found on the page at all.');
        }
      }

      this.buyButtonForms = forms;
    }

    lockBuyButtons() {
      (this.buyButtonForms || []).forEach((form) => {
        form.classList.add('xd-buy-buttons--locked');
        form.setAttribute('aria-disabled', 'true');

        const addToCartButton = form.querySelector('[ref="addToCartButton"]') || form.querySelector('button[type="submit"]');
        if (addToCartButton) addToCartButton.disabled = true;
      });

      if (this.hint) this.hint.hidden = false;
    }

    unlockBuyButtonsUI() {
      (this.buyButtonForms || []).forEach((form) => {
        form.classList.remove('xd-buy-buttons--locked');
        form.removeAttribute('aria-disabled');

        const addToCartButton = form.querySelector('[ref="addToCartButton"]') || form.querySelector('button[type="submit"]');
        if (addToCartButton) addToCartButton.disabled = false;
      });

      if (this.hint) this.hint.hidden = true;
    }

    /**
     * @param {string} optionName
     * @returns {HTMLSelectElement|null}
     */
    getNativeSelect(optionName) {
      if (!this.nativePicker) return null;
      return this.nativePicker.querySelector('select[name="options[' + CSS.escape(optionName) + ']"]');
    }

    /**
     * Sets a native <select>'s value, keeps the `selected` attribute in sync
     * (the native buildRequestUrl reads the `selected` attribute, not .value),
     * and optionally dispatches a real `change` event.
     * @param {HTMLSelectElement} select
     * @param {string} value
     * @param {boolean} dispatch
     * @returns {boolean} Whether a matching option was found and applied.
     */
    setNativeSelectValue(select, value, dispatch) {
      if (!select) return false;

      const options = Array.from(select.options);
      const target = options.find((option) => option.value === value);
      if (!target) return false;

      options.forEach((option) => option.removeAttribute('selected'));
      target.setAttribute('selected', 'selected');
      select.value = value;

      if (dispatch) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return true;
    }

    /**
     * Pushes the chosen Model + Colour into the native picker's <select>
     * elements and fires a single native `change` event on the last one, so
     * Shopify resolves and applies the real variant.
     * @param {{model: string, colour: string}} variant
     */
    syncNativePicker(variant) {
      if (!this.nativePicker) {
        this.findNativePicker();
      }
      if (!this.nativePicker) {
        console.warn('[xd-vehicle-selector] Cannot sync — native <variant-picker> not found.', this.config);
        return;
      }

      const optionNames = Array.isArray(this.config.optionNames) ? this.config.optionNames : [];
      if (optionNames.length < 2) {
        console.warn('[xd-vehicle-selector] Expected 2 option names (Model, Colour), got:', optionNames);
        return;
      }

      const values = [variant.model, variant.colour];
      let dispatchedOk = false;

      optionNames.forEach((optionName, index) => {
        const select = this.getNativeSelect(optionName);
        const isLast = index === optionNames.length - 1;

        if (!select) {
          console.warn(
            '[xd-vehicle-selector] Native <select name="options[' +
              optionName +
              ']"> not found inside <variant-picker>. Sync for this option will be skipped.',
          );
          return;
        }

        const applied = this.setNativeSelectValue(select, values[index], isLast);
        if (!applied) {
          console.warn(
            '[xd-vehicle-selector] No matching <option value="' +
              values[index] +
              '"> found in the native "' +
              optionName +
              '" select. Available values:',
            Array.from(select.options).map((o) => o.value),
          );
        } else if (isLast) {
          dispatchedOk = true;
        }
      });

      if (dispatchedOk) {
        console.info('[xd-vehicle-selector] Dispatched native change event for variant', variant);
      } else {
        console.warn('[xd-vehicle-selector] Native change event was NOT dispatched — sync failed above.');
      }
    }

    // ---------------------------------------------------------------------
    // Rendering
    // ---------------------------------------------------------------------

    populateModels() {
      const models = this.uniqueSorted(this.variants.map((v) => v.model));

      this.modelSelect.innerHTML =
        '<option value="">Select model</option>' +
        models.map((model) => '<option value="' + escapeHtml(model) + '">' + escapeHtml(model) + '</option>').join('');

      this.modelSelect.disabled = models.length === 0;
    }

    /**
     * @param {string} model
     */
    populateColours(model) {
      this.coloursContainer.innerHTML = '';

      if (!model) {
        this.setColoursEmptyMessage('Select a model to see available colours.');
        return;
      }

      const colourVariants = this.variants
        .filter((v) => v.model === model && v.available)
        .sort((a, b) => (a.colour || '').localeCompare(b.colour || ''));

      if (!colourVariants.length) {
        this.setColoursEmptyMessage('No colours are currently available for this model.');
        return;
      }

      this.hideColoursEmpty();

      const fragment = document.createDocumentFragment();
      colourVariants.forEach((variant) => {
        fragment.appendChild(this.buildColourCard(variant));
      });
      this.coloursContainer.appendChild(fragment);

      this.markActiveColour(this.state.variantId);
    }

    /**
     * @param {object} variant
     * @returns {HTMLButtonElement}
     */
    buildColourCard(variant) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'xd-colour-card';
      button.dataset.variantId = String(variant.id);
      button.setAttribute('aria-pressed', 'false');

      const imageWrap = document.createElement('span');
      imageWrap.className = 'xd-colour-card__image-wrap';

      if (variant.image) {
        const img = document.createElement('img');
        img.src = variant.image;
        img.alt = variant.colour || '';
        img.loading = 'lazy';
        img.width = 120;
        img.height = 120;
        imageWrap.appendChild(img);
      }

      const tick = document.createElement('span');
      tick.className = 'xd-colour-card__tick';
      tick.setAttribute('aria-hidden', 'true');
      tick.innerHTML = TICK_SVG;
      imageWrap.appendChild(tick);

      const name = document.createElement('span');
      name.className = 'xd-colour-card__name';
      name.textContent = variant.colour || '';

      button.appendChild(imageWrap);
      button.appendChild(name);

      return button;
    }

    /**
     * @param {string|number|null} variantId
     */
    markActiveColour(variantId) {
      const cards = this.coloursContainer.querySelectorAll('.xd-colour-card');
      cards.forEach((card) => {
        const isActive = variantId != null && String(card.dataset.variantId) === String(variantId);
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-pressed', String(isActive));
      });
    }

    /**
     * @param {string} message
     */
    setColoursEmptyMessage(message) {
      if (!this.coloursEmpty) return;
      this.coloursEmpty.textContent = message;
      this.coloursEmpty.hidden = false;
    }

    hideColoursEmpty() {
      if (!this.coloursEmpty) return;
      this.coloursEmpty.hidden = true;
    }

    // ---------------------------------------------------------------------
    // Events
    // ---------------------------------------------------------------------

    bindEvents() {
      this.modelSelect.addEventListener('change', () => {
        this.state.model = this.modelSelect.value;
        this.state.variantId = null;

        this.populateColours(this.state.model);
        this.lockBuyButtons();
      });

      this.coloursContainer.addEventListener('click', (event) => {
        const card = event.target.closest('.xd-colour-card');
        if (!card) return;

        this.selectVariant(card.dataset.variantId);
      });
    }

    /**
     * @param {string} variantId
     */
    selectVariant(variantId) {
      const variant = this.variants.find((v) => String(v.id) === String(variantId));
      if (!variant) {
        console.warn('[xd-vehicle-selector] selectVariant called with unknown variantId:', variantId);
        return;
      }

      console.info('[xd-vehicle-selector] Colour card clicked:', variant);

      this.state.variantId = variant.id;
      this.markActiveColour(variant.id);
      this.syncNativePicker(variant);
      this.unlockBuyButtonsUI();
    }

    // ---------------------------------------------------------------------
    // Utilities
    // ---------------------------------------------------------------------

    /**
     * @param {Array<string|undefined>} values
     * @returns {string[]}
     */
    uniqueSorted(values) {
      return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
    }
  }

  function init() {
    document.querySelectorAll('[data-xd-vehicle-selector]').forEach((root) => {
      if (root.dataset.xdInitialized === 'true') return;
      root.dataset.xdInitialized = 'true';
      new XdVehicleSelector(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();